import { useState, useEffect, useCallback, useMemo } from 'react';
import { zipfileService } from '../services/zipfileService';
import { getZipfileSummaryFields } from '@/features/structures/config/fieldDefinitions';

export function useZipfilesData() {
  const [zipfiles, setZipfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState({});
  
  // Fetch all zipfiles with pagination
  const fetchZipfiles = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    setCurrentFilters(filters);
    
    try {
      const limit = 1000;
      let allItems = [];
      let offset = 0;
      let hasMore = true;
      let total = 0;
      
      // Keep fetching until we have all data
      while (hasMore) {
        const response = await zipfileService.fetchAll({
          offset,
          limit,
          ...filters
        });
        
        const items = response.items || [];
        allItems = [...allItems, ...items];
        
        // Check if there are more items to fetch
        total = response.total || response.count || 0;
        hasMore = allItems.length < total;
        offset += limit;
      }
      
      // Update state once with all data
      setZipfiles(allItems);
      setTotalCount(total);
      console.log(`Fetched all zipfiles: ${allItems.length} total`);
    } catch (err) {
      console.error('Failed to fetch zipfiles:', err);
      setError(err.message || 'Failed to load zipfiles');
      setZipfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Remove initial load to prevent duplicate fetching
  // InputsTab will handle the initial fetch
  
  // Refresh function maintains current filters
  const refresh = useCallback(() => {
    fetchZipfiles(currentFilters);
  }, [currentFilters, fetchZipfiles]);
  
  // Transform zipfiles and FMO inputs into tree data structure
  // This should be called ONCE when data is loaded, not on every render
  const transformToTreeData = useCallback((zipfilesData, fmoInputsData) => {
    // Get fields that need summary calculation
    const summaryFields = getZipfileSummaryFields();
    
    // Helper to get nested value from FMO input using path like 'prep.project' or 'clusterspec.name'
    const getFMOInputValue = (fmoInput, path) => {
      if (!path) return null;
      return path.split('.').reduce((obj, key) => obj?.[key], fmoInput);
    };
    
    // Helper to calculate summary value as display-ready string
    const calculateSummaryValue = (fmoInputs, field) => {
      const values = new Set();
      
      fmoInputs.forEach(fmoInput => {
        const value = getFMOInputValue(fmoInput, field.entryPath);
        if (value) values.add(value);
      });
      
      // Return display-ready strings
      if (values.size === 0) return '';
      if (values.size === 1) return Array.from(values)[0];
      return `varied (${values.size})`;
    };
    
    const treeRows = [];
    
    // Handle case when FMO inputs aren't loaded yet
    if (!fmoInputsData || fmoInputsData.length === 0) {
      // Return parent rows without spec rows and without summary values
      return zipfilesData.map(zipfile => ({
        ...zipfile,
        id_: String(zipfile.id_),  // Ensure id_ is string
        path: [String(zipfile.id_)],  // Convert to string for tree mode
        isParentRow: true
      }));
    }
    
    zipfilesData.forEach(zipfile => {
      // Get FMO inputs for this zipfile
      const zipfileFMOInputs = fmoInputsData.filter(fmoInput => 
        zipfile.entry_ids?.includes(fmoInput.id_)
      );
      
      // Build parent row with all summary values pre-calculated
      const parentRow = {
        ...zipfile,
        id_: String(zipfile.id_),  // Ensure id_ is string for consistent filtering
        path: [String(zipfile.id_)],  // Convert to string for tree mode
        isParentRow: true,
        entry_count: zipfile.entry_ids?.length || 0  // Add entry_count directly
      };
      
      // Add summary values for all flagged fields
      summaryFields.forEach(field => {
        parentRow[field.fieldName] = calculateSummaryValue(zipfileFMOInputs, field);
      });
      
      treeRows.push(parentRow);
      
      // Group entries by spec combination
      const specGroups = {};
      zipfileFMOInputs.forEach(fmoInput => {
        const clusterspec = fmoInput.clusterspec?.name || 'Unknown';
        const pocketspec = fmoInput.pocketspec?.name || 'Unknown';
        const inputspec = fmoInput.inputspec?.name || 'Unknown';
        const specKey = `${clusterspec}|${pocketspec}|${inputspec}`;
        
        if (!specGroups[specKey]) {
          specGroups[specKey] = {
            clusterspec,
            pocketspec,
            inputspec,
            fmoInputs: [],
            entry_ids: []
          };
        }
        specGroups[specKey].fmoInputs.push(fmoInput);
        specGroups[specKey].entry_ids.push(fmoInput.id_);
      });
      
      // Create spec rows as children
      Object.keys(specGroups).forEach((specKey, index) => {
        const group = specGroups[specKey];
        const specId = `${zipfile.id_}-${index + 1}`;  // Simple format: parentId-index (1-based)
        
        // Inherit parent fields but override specific ones
        const specRow = {
          ...zipfile,  // Inherit all parent fields first
          id_: specId,  // Use simple ID format for spec rows
          path: [String(zipfile.id_), specId],  // Convert to strings for tree mode
          parentId: String(zipfile.id_),  // Ensure parentId is also a string
          isSpecRow: true,
          
          // Direct spec values (these override any inherited values)
          clusterspec: group.clusterspec,
          pocketspec: group.pocketspec,
          inputspec: group.inputspec,
          
          // Entry metadata
          entry_ids: group.entry_ids,
          entry_count: group.fmoInputs.length,
          
          // Remove name for child rows - leave it empty
          name: '',  // Empty name for child rows
          
          // Don't show download button for spec rows
          noDownload: true
        };
        
        // Add summary values for spec row (except spec fields which are already set)
        summaryFields.forEach(field => {
          // Skip spec fields since they have direct values
          if (!['clusterspec', 'pocketspec', 'inputspec'].includes(field.fieldName)) {
            specRow[field.fieldName] = calculateSummaryValue(group.fmoInputs, field);
          }
        });
        
        treeRows.push(specRow);
      });
    });
    
    return treeRows;
  }, []);
  
  return {
    zipfiles,
    loading,
    error,
    totalCount,
    fetchZipfiles,
    refresh,
    transformToTreeData
  };
}